"use client";

import { cn } from "@/lib/utils";
import { COUNTER_VISIBLE_FROM, type DraftStore, MAX_COMPOSER_CHARS, useDraft } from "./draft-store";
import { MorphSendButton } from "./morph-send-button";

/**
 * Right-hand side of the composer toolbar: the char counter and the
 * mic / send button. The only parts of the tab that depend on the live draft,
 * so they subscribe to it themselves and the rest of the tab (and with it the
 * whole message list) stays untouched by typing.
 */
export function ComposerSendControls({
  draft,
  hasAttachments,
  busy,
  onSend,
  onMic,
  ariaLabels,
}: {
  draft: DraftStore;
  /** Any staged attachment (even one still uploading) also makes it a SEND. */
  hasAttachments: boolean;
  /** A send or an attachment upload is in flight. */
  busy: boolean;
  onSend: () => void;
  onMic: () => void;
  ariaLabels: { send: string; mic: string };
}) {
  const hasText = useDraft(draft, (d) => d.text.trim().length > 0);
  // 0 below the threshold, so ordinary typing never changes the selected value.
  const counterLength = useDraft(draft, (d) =>
    d.text.length >= COUNTER_VISIBLE_FROM ? d.text.length : 0,
  );
  const overLimit = useDraft(draft, (d) => d.text.length > MAX_COMPOSER_CHARS);
  return (
    <div className="flex items-center gap-2">
      {/* Char counter - only near/over the cap so it's not noise on normal short
          messages. Red once over; send is blocked. Counts VISIBLE text (not HTML
          markup), matching the send guard + the BE's tag-stripped length check. */}
      {counterLength > 0 && (
        <span
          className={cn(
            "text-[11px] tabular-nums",
            overLimit ? "text-wx-danger" : "text-wx-fg-subtle",
          )}
        >
          {counterLength}/{MAX_COMPOSER_CHARS}
        </span>
      )}
      <MorphSendButton
        // Show SEND as soon as there's anything to send - text OR any attached
        // file, even if the upload is still in flight.
        kind={hasText || hasAttachments ? "send" : "mic"}
        disabled={busy || overLimit}
        onSend={onSend}
        onMic={onMic}
        ariaLabels={ariaLabels}
      />
    </div>
  );
}
